import React from "react";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    Location,
    LocationProximity,
    isShowroom,
    isTechnicurean,
} from "../../models";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { Address } from "./Address";
import { Distance } from "./Distance";
import { IndexGlyph } from "./IndexGlyph";
import { PhoneNumber } from "./PhoneNumber";
import { ShowroomGlyph } from "./ShowroomGlyph";

import styles from "./LocationListItem.module.scss";

const iOS = () =>
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));

export type LocationListItemProps = LocationProximity & {
    index: number;
    focused: boolean;
    onSelect: () => void;
};

const LocationGlyph = (props: { location: Location; index: number }) => {
    if (isShowroom(props.location)) {
        return <ShowroomGlyph />;
    }
    if (isTechnicurean(props.location)) {
        return (
            <IndexGlyph
                index={props.index}
                pinClassName={styles.technicureanPin}
            />
        );
    }
    return <IndexGlyph index={props.index} />;
};

export const LocationListItem = (props: LocationListItemProps) => {
    const mapQuery = encodeURIComponent(
        `${props.location.display_name}, ${props.location.address_summary}`,
    );
    const mapLink = iOS()
        ? `https://maps.apple.com/?daddr=${mapQuery}`
        : `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

    const showroom = isShowroom(props.location);

    return (
        <li
            className={concatClassNames([
                styles.li,
                props.focused ? styles.focused : undefined,
            ])}
            data-id={props.location.id}
            onClick={props.onSelect}
        >
            <LocationGlyph location={props.location} index={props.index} />
            <div className={styles.contactInfo}>
                <Clickable
                    onPress={props.onSelect}
                    className={styles.clickable}
                >
                    <h3
                        className={
                            showroom ? styles.showroomName : styles.locationName
                        }
                    >
                        {props.location.display_name}
                    </h3>
                </Clickable>
                <Address location={props.location} />
                {props.location.phone && (
                    <p>
                        <PhoneNumber phone={props.location.phone} />
                    </p>
                )}
                {props.location.locator_listing_note && (
                    <p className={styles.text}>
                        {props.location.locator_listing_note}
                    </p>
                )}
                {showroom && props.location.website_url ? (
                    <Clickable
                        href={props.location.website_url}
                        className={styles.textLink}
                    >
                        {t`Learn More about Experience Centers`}
                    </Clickable>
                ) : (
                    props.location.website_url && (
                        <Clickable
                            href={props.location.website_url}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.itemWithIcon}
                        >
                            <Svg
                                name="external"
                                aria-hidden="true"
                                className={styles.external}
                            />
                            <span className={styles.textLink}>
                                {props.location.website_url}
                            </span>
                        </Clickable>
                    )
                )}
            </div>
            <Distance distance={props.distance} mapLink={mapLink} />
        </li>
    );
};
